import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * URL-synced state hook. Refreshing the page or sharing the URL preserves
 * the filter/search state.
 */
export function useUrlState<T extends Record<string, string | undefined>>(
  defaults: T,
): [T, (patch: Partial<T>) => void] {
  const [params, setParams] = useSearchParams();

  const state = useMemo(() => {
    const out: any = { ...defaults };
    for (const [k] of Object.entries(defaults)) {
      const v = params.get(k);
      if (v !== null) out[k] = v;
    }
    return out as T;
  }, [params, defaults]);

  const setState = useCallback(
    (patch: Partial<T>) => {
      const next = new URLSearchParams(params);
      for (const [k, v] of Object.entries(patch)) {
        if (v === undefined || v === '' || v === defaults[k]) next.delete(k);
        else next.set(k, String(v));
      }
      setParams(next, { replace: true });
    },
    [params, setParams, defaults],
  );

  return [state, setState];
}
